/**
 * Accessibility Utilities
 * Common functions and utilities for enhancing accessibility
 */

/**
 * Announce message to screen readers
 * Creates a temporary element with aria-live to announce dynamic content
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}

/**
 * Generate unique IDs for form elements
 * Ensures proper association between labels and inputs
 */
export function generateId(prefix = 'element'): string {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Check if an element is focusable
 */
export function isFocusable(element: HTMLElement): boolean {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ];

  const matches = focusableSelectors.some(selector =>
    element.matches(selector)
  );

  if (!matches) return false;

  // Check if element is visible
  const style = window.getComputedStyle(element);
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    element.offsetWidth > 0 &&
    element.offsetHeight > 0
  );
}

/**
 * Get all focusable elements within a container
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(', ');

  return Array.from(container.querySelectorAll(focusableSelectors)).filter(
    element => isFocusable(element as HTMLElement)
  ) as HTMLElement[];
}

/**
 * Get descriptive text for form validation errors
 */
export function getErrorDescription(error: string): string {
  // Add contextual information for screen readers
  return `Error: ${error}. Please correct this field and try again.`;
}

/**
 * Get descriptive text for loading states
 */
export function getLoadingDescription(action: string): string {
  return `${action} Please wait...`;
}

/**
 * Create ARIA description for password requirements
 */
export function getPasswordRequirements(): string {
  return 'Password must be at least 8 characters long and contain a mix of letters and numbers.';
}

/**
 * Create ARIA description for form submission status
 */
export function getSubmissionStatus(
  isLoading: boolean,
  error?: string,
  success?: boolean
): string {
  if (isLoading) {
    return 'Form is being submitted. Please wait.';
  }

  if (error) {
    return `Form submission failed: ${error}`;
  }

  if (success) {
    return 'Form submitted successfully.';
  }

  return 'Form ready for submission.';
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if user is using high contrast mode
 */
export function prefersHighContrast(): boolean {
  if (typeof window === 'undefined') return false;

  return window.matchMedia('(prefers-contrast: high)').matches;
}

/**
 * Keyboard event handlers for accessibility
 */
export const keyboardHandlers = {
  /**
   * Handle Enter and Space key presses for button-like elements
   */
  onActivate: (callback: () => void) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  },

  /**
   * Handle Escape key for closing modals/dropdowns
   */
  onEscape: (callback: () => void) => (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      callback();
    }
  },

  /**
   * Handle arrow keys for navigation
   */
  onArrowNavigation:
    (
      onUp: () => void,
      onDown: () => void,
      onLeft?: () => void,
      onRight?: () => void
    ) =>
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          onUp();
          break;
        case 'ArrowDown':
          event.preventDefault();
          onDown();
          break;
        case 'ArrowLeft':
          if (onLeft) {
            event.preventDefault();
            onLeft();
          }
          break;
        case 'ArrowRight':
          if (onRight) {
            event.preventDefault();
            onRight();
          }
          break;
      }
    },
};

/**
 * ARIA live region management
 */
export class LiveRegionManager {
  private static instance: LiveRegionManager;
  private politeRegion: HTMLElement | null = null;
  private assertiveRegion: HTMLElement | null = null;

  static getInstance(): LiveRegionManager {
    if (!LiveRegionManager.instance) {
      LiveRegionManager.instance = new LiveRegionManager();
    }
    return LiveRegionManager.instance;
  }

  private getPoliteRegion(): HTMLElement {
    if (!this.politeRegion) {
      this.politeRegion = this.createLiveRegion('polite');
    }
    return this.politeRegion;
  }

  private getAssertiveRegion(): HTMLElement {
    if (!this.assertiveRegion) {
      this.assertiveRegion = this.createLiveRegion('assertive');
    }
    return this.assertiveRegion;
  }

  private createLiveRegion(priority: 'polite' | 'assertive'): HTMLElement {
    const region = document.createElement('div');
    region.setAttribute('aria-live', priority);
    region.setAttribute('aria-atomic', 'true');
    region.className = 'sr-only';
    region.id = `live-region-${priority}`;
    document.body.appendChild(region);
    return region;
  }

  announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const region =
      priority === 'assertive'
        ? this.getAssertiveRegion()
        : this.getPoliteRegion();

    // Clear and set new message
    region.textContent = '';
    setTimeout(() => {
      region.textContent = message;
    }, 100);
  }

  clear(priority?: 'polite' | 'assertive'): void {
    if (!priority || priority === 'polite') {
      if (this.politeRegion) {
        this.politeRegion.textContent = '';
      }
    }

    if (!priority || priority === 'assertive') {
      if (this.assertiveRegion) {
        this.assertiveRegion.textContent = '';
      }
    }
  }
}
